import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axiosInstance from '../../../Config/axiosInstance';
import AddHRDocumentRegistry from './AddHRDocumentRegistry';
import EditHRDocumentRegistry from './EditHRDocumentRegistry';
import VisibilityIcon from '@mui/icons-material/Visibility';

import './HRDocumentRegistry.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box
} from '@mui/material';

// Definire alertă personalizată pentru Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Funcție pentru formatarea datei în format românesc dd.mm.yyyy
const formatDateToRomanian = (dateString) => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

function HRDocumentRegistry() {
  // Stări pentru datele formularului, fișiere, erori și mesaje de confirmare
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  // Stare pentru a reține ce documente au fișiere atașate
  const [documentsWithFiles, setDocumentsWithFiles] = useState({});

  const navigate = useNavigate();


  const [downloadErrorOpen, setDownloadErrorOpen] = useState(false);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState('');



  const getPagePermissions = (path) => {
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];
    return permissions.find((page) => new URL(page.url).pathname === path) || {};
  };

  // Obține permisiunile paginii din localStorage
  useEffect(() => {
    const { can_create, can_edit, can_delete } = getPagePermissions("/hr-document-registry");
    setCanCreate(can_create);
    setCanEdit(can_edit);
    setCanDelete(can_delete);
  }, []);

  // Funcție pentru a verifica dacă un document are fișier atașat
  const checkIfDocumentHasFile = async (documentId) => {
    try {
      // Folosim HEAD request pentru a verifica existența fișierului fără a-l descărca
      await axiosInstance.head(`/hr-document-registry/${documentId}/download/`);
      // Dacă request-ul HEAD reușește (status 200), înseamnă că există fișier
      return true;
    } catch (error) {
      // Dacă primim 404, înseamnă că nu există fișier
      if (error.response && error.response.status === 404) {
        return false;
      }
      // Pentru alte erori, considerăm că nu există fișier
      console.warn(`Eroare la verificarea documentului ${documentId}:`, error.message);
      return false;
    }
  };

  // Fetch la documente și utilizatori → formatează și setează datele pentru tabel
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [documentResponse, usersResponse] = await Promise.all([
          axiosInstance.get('hr-document-registry/'),
          axiosInstance.get('users/'),
        ]);

        const userMap = usersResponse.data.reduce((acc, user) => {
          acc[user.id] = `${user.first_name} ${user.last_name}`;
          return acc;
        }, {});

        const data = documentResponse.data.map((item) => ({
          id: item.id,
          code: item.code || '-',
          date: formatDateToRomanian(item.date), // Formatăm data în format românesc
          content: item.content || '-',
          direction: item.direction || '-',
          sender: item.sender || '-',
          recipient: item.recipient || '-',
          user: userMap[item.user] || '-',
          rawDate: item.date // Păstrăm data originală pentru sortare
        }));

        const sortedData = data.sort((a, b) => b.id - a.id);

        if (isMounted) {
          setRows(sortedData);
          
          // Verifică pentru fiecare document dacă are fișier atașat
          const fileStatusPromises = sortedData.map(async (item) => {
            const hasFile = await checkIfDocumentHasFile(item.id);
            return { id: item.id, hasFile };
          });

          const fileStatuses = await Promise.all(fileStatusPromises);
          const fileStatusMap = fileStatuses.reduce((acc, { id, hasFile }) => {
            acc[id] = hasFile;
            return acc;
          }, {});

          setDocumentsWithFiles(fileStatusMap);
        }
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Navighează către pagina de adăugare
  const handleAddClick = () => navigate('/hr-document-registry/add-document-registry');

  // Navighează către pagina de editare
  const handleEditClick = (row) => navigate(`/hr-document-registry/edit-document-registry/${row.id}`);
  // Șterge un document după confirmare și actualizează lista
  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/hr-document-registry/${itemToDelete.id}/`);
      setRows(prev => prev.filter(r => r.id !== itemToDelete.id));
      setOpenDeleteDialog(false);
    } catch (error) {
      const message = error.response?.data?.error ||
        error.response?.data?.detail ||
        "An unexpected error occurred while deleting.";
      setDeleteErrorMessage(message);
      setOpenDeleteDialog(false);
      setErrorDialogOpen(true);
    }
  };

  // Deschide dialogul de confirmare pentru ștergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  // Descarcă documentul aferent unui rând, tratând și erorile
  const handleDownloadClick = async (row) => {
    try {
      const response = await axiosInstance.get(`/hr-document-registry/${row.id}/download/`, {
        responseType: 'blob',
      });

      const extensionMap = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'image/jpeg': 'jpg',
        'image/png': 'png',
      };

      const extractFilename = (disposition) => {
        if (!disposition) return null;

        try {
          const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
          if (utf8Match) return decodeURIComponent(utf8Match[1].trim());

          const quotedMatch = disposition.match(/filename="([^"]+)"/i);
          if (quotedMatch) return quotedMatch[1];

          const simpleMatch = disposition.match(/filename=([^;\n]+)/i);
          if (simpleMatch) return simpleMatch[1].trim();
        } catch (e) {
          console.warn("Eroare la parsarea filename:", e);
        }

        return null;
      };

      let filename;
      const disposition = response.headers && response.headers['content-disposition'];
      filename = extractFilename(disposition);
      console.log("📄 Filename extras:", filename);

      if (!filename) {
        filename = `document_${row.code}`;
      }

      const mimeType = response.headers['content-type'];
      const expectedExtension = extensionMap[mimeType];
      const currentExtension = filename.split('.').pop().toLowerCase();

      if (expectedExtension && currentExtension !== expectedExtension) {
        filename = filename.replace(/\.[^/.]+$/, '') + `.${expectedExtension}`;
      } else if (!filename.includes('.') && expectedExtension) {
        filename += `.${expectedExtension}`;
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('❌ Download failed:', error);

      let message = 'Failed to download the document.';
      if (error.response) {
        if (error.response.status === 404) {
          message = 'No attached document found for download.';
        } else if (error.response.data?.error) {
          message = error.response.data.error;
        }
      }

      setDownloadErrorMessage(message);
      setDownloadErrorOpen(true);
    }
  };

  const handlePreviewClick = async (row) => {
  try {
    const response = await axiosInstance.get(`/hr-document-registry/${row.id}/download/`, {
      responseType: 'blob',
    });

    const mimeType = response.headers['content-type'];

    if (mimeType !== 'application/pdf') {
      setDownloadErrorMessage('Preview is only available for PDF files.');
      setDownloadErrorOpen(true);
      return;
    }

    const disposition = response.headers['content-disposition'];
    const extractFilename = (disposition) => {
      if (!disposition) return null;

      try {
        const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
        if (utf8Match) return decodeURIComponent(utf8Match[1].trim());

        const quotedMatch = disposition.match(/filename="([^"]+)"/i);
        if (quotedMatch) return quotedMatch[1];

        const simpleMatch = disposition.match(/filename=([^;\n]+)/i);
        if (simpleMatch) return simpleMatch[1].trim();
      } catch (e) {
        console.warn("Eroare la parsarea filename:", e);
      }

      return null;
    };

    const filename = extractFilename(disposition) || `document_${row.code}.pdf`;

    const file = new Blob([response.data], { type: mimeType });
    const fileURL = URL.createObjectURL(file);

    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(`
        <html>
          <head>
            <title>${filename}</title>
          </head>
          <body style="margin:0">
            <embed src="${fileURL}" type="application/pdf" width="100%" height="100%" />
          </body>
        </html>
      `);
      previewWindow.document.close();
    }
  } catch (error) {
    console.error('❌ Preview failed:', error);
    setDownloadErrorMessage('No attached document was found.');
    setDownloadErrorOpen(true);
  }
};

  // Coloanele tabelului (inclusiv acțiuni: edit, delete, download, preview)
  const columns = [
    { field: 'code', headerName: 'Code', flex: 1, minWidth: 170 },
    { 
      field: 'date', 
      headerName: 'Date', 
      flex: 1, 
      minWidth: 110,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {params.value}
        </Box>
      ),
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = rows.find(r => r.id === param1.id);
        const row2 = rows.find(r => r.id === param2.id);
        if (!row1 || !row2) return 0;
        
        const date1 = new Date(row1.rawDate || '');
        const date2 = new Date(row2.rawDate || '');
        return date1.getTime() - date2.getTime();
      }
    },
    { field: 'direction', headerName: 'Direction', flex: 1, minWidth: 145 },
    { field: 'sender', headerName: 'Sender', flex: 1, minWidth: 130 },
    { field: 'recipient', headerName: 'Recipient', flex: 1, minWidth: 150 },
    { field: 'content', headerName: 'Content', flex: 2, minWidth: 200 },
    { field: 'user', headerName: 'User', flex: 1, minWidth: 110 },
  ];

  columns.push({
    field: 'action',
    headerName: 'Action',
    width: 180, 
    renderCell: (params) => (
      <>
        {canEdit && (
          <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
            <EditNoteSharpIcon />
          </IconButton>
        )}
        {canDelete && (
          <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
            <DeleteIcon />
          </IconButton>
        )}
        {/* Iconița de download - verde dacă există document, neagră dacă nu */}
        <IconButton 
          onClick={() => handleDownloadClick(params.row)} 
          sx={{ 
            color: documentsWithFiles[params.row.id] ? '#4caf50' : 'black',
            opacity: documentsWithFiles[params.row.id] ? 1 : 0.5
          }}
        >
          <DownloadIcon />
        </IconButton>
        <IconButton onClick={() => handlePreviewClick(params.row)} sx={{ color: '#093d71ff' }}>
          <VisibilityIcon />
        </IconButton>
      </>
    ),
    sortable: false,
    disableColumnMenu: true,
  });



  return (
    <Routes>
      {/* Pagina principală: tabelul cu documente */}
      <Route path="/" element={
        <div className="hr_document-registry-page">
          <div className="document-registry-container">
            <h2 className='title'>DOCUMENT REGISTRY</h2>
            {canCreate && (
              <div className="add-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{ float: 'right', marginBottom: '40px' }}
                >
                  New
                </Button>

              </div>
            )}
          </div>

          <div className="table-container">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 15, 20, 25]}
              rowHeight={38}
              sx={{
                maxHeight: 500,
                borderRadius: '8px',
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
            />
          </div>

          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            PaperProps={{
              sx: {
                borderRadius: '10px',
                px: 2,
                py: 1,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                minWidth: 200,
              },
            }}
          >
            <DialogTitle
              sx={{
                textAlign: 'left',
                fontWeight: 600,
                fontSize: '1.25rem',
                color: '#333',
              }}
            >
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <DialogContentText
                sx={{
                  textAlign: 'left',
                  fontSize: '0.95rem',
                  color: '#666',
                  px: 0,
                }}
              >
                Are you sure you want to delete this record?
              </DialogContentText>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                variant="contained"
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 100 },
                  mb: { xs: 1, sm: 0 },
                  '&:hover': { backgroundColor: '#d5d5d5' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 100 },
                  ml: { xs: 0, sm: 2 },
                  '&:hover': { backgroundColor: '#b71c1c' },
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} className="error-dialog">
            <DialogTitle className="error-dialog-title">Delete Failed</DialogTitle>
            <DialogContent>
              <DialogContentText className="error-dialog-text">
                {deleteErrorMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions className="error-dialog-actions">
              <Button onClick={() => setErrorDialogOpen(false)} className="close-error-button">Close</Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={downloadErrorOpen}
            autoHideDuration={3000}
            onClose={() => setDownloadErrorOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            className="custom-snackbar"
          >
            <Alert
              onClose={() => setDownloadErrorOpen(false)}
              severity="error"
              sx={{ width: '100%' }}
            >
              {downloadErrorMessage}
            </Alert>
          </Snackbar>
        </div>
      } />

      <Route path="add-document-registry" element={canCreate ? <AddHRDocumentRegistry /> : <h3>Unauthorized</h3>} />
      <Route path="edit-document-registry/:id" element={canEdit ? <EditHRDocumentRegistry /> : <h3>Unauthorized</h3>} />
    </Routes>
  );
}

export default HRDocumentRegistry;